import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cx } from "class-variance-authority";
import React, { useId } from "react";

import * as styles from "./Checkbox.module.scss";
import { ReactComponent as Check } from "./tick.svg";

export type CheckboxProps = {
  label?: React.ReactNode;
  hasError?: boolean;
  noSpacing?: boolean;
  variant?: "secondary" | "primary";
  size?: "default" | "small";
  containerClassName?: string;
  labelClassName?: string;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  labelComponent?: React.ElementType;
  children?: React.ReactNode;
} & CheckboxPrimitive.CheckboxProps;

const Checkbox = ({
  ref,
  className,
  containerClassName,
  noSpacing,
  label,
  labelClassName,
  labelProps,
  labelComponent,
  id: idProp,
  disabled,
  hasError,

  // primary will act as normal and fill, secondary is bordered.
  variant = "primary",

  size = "default",
  children,
  ...props
}: React.ComponentPropsWithoutRef<
  React.ForwardRefExoticComponent<CheckboxProps>
> & {
  ref?: React.Ref<React.ElementRef<typeof CheckboxPrimitive.Root>>;
}) => {
  const id = useId();
  const LabelComponent = labelComponent ?? "label";

  return (
    <div
      className={cx(styles.container, containerClassName, {
        [styles.disabled]: disabled,
        [styles.error]: hasError,
        [styles.noSpacing]: noSpacing,
        [styles.border]: variant === "secondary",
        [styles.solidWithChildren]: variant === "primary" && children,
        [styles.small]: size === "small",
      })}
    >
      <CheckboxPrimitive.Root
        ref={ref}
        className={cx(styles.checkbox, className)}
        id={idProp ?? id}
        disabled={disabled}
        {...props}
      >
        {children || (
          <CheckboxPrimitive.Indicator
            className={cx({ [styles.iconContainer]: variant === "primary" })}
          >
            <Check className={styles.icon} />
          </CheckboxPrimitive.Indicator>
        )}
      </CheckboxPrimitive.Root>
      {label && (
        <LabelComponent
          htmlFor={idProp ?? id}
          className={cx(styles.label, labelClassName)}
          {...labelProps}
        >
          {label}
        </LabelComponent>
      )}
    </div>
  );
};

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
